import { ReportBreakdownChart } from '@/components/reports/ReportBreakdownChart';
import { useEffect, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Card, SectionHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import { StatTile } from '@/components/StatTile';
import { reportsService, BusinessReport, WinLossSummary, DigestSubscription } from '@/services/reports.service';
import { Select } from '@/components/Select';
import toast from 'react-hot-toast';
import {
  BarChart3, Users, Trophy, Send, Sparkles, FileText, Download, Calendar, AlertCircle, X as XIcon, TrendingUp,
  Mail,
} from 'lucide-react';

interface DateRange { from?: string; to?: string; label: string }

function todayIso() { return new Date().toISOString().split('T')[0]; }
function daysAgoIso(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}
function startOfMonthIso() {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().split('T')[0];
}
function startOfYearIso() {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), 0, 1)).toISOString().split('T')[0];
}

const PRESETS: DateRange[] = [
  { label: 'Last 7d',      from: daysAgoIso(7),  to: todayIso() },
  { label: 'Last 30d',     from: daysAgoIso(30), to: todayIso() },
  { label: 'This month',   from: startOfMonthIso(), to: todayIso() },
  { label: 'This year',    from: startOfYearIso(), to: todayIso() },
  { label: 'Last 90d',     from: daysAgoIso(90), to: todayIso() },
];

const fmtNum = (n: number) => n.toLocaleString();
const fmtPct = (n: number) => `${n.toFixed(1)}%`;
const fmtUsd = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function Reports() {
  const [active, setActive] = useState<string>('Last 30d');
  const [filter, setFilter] = useState<{ from?: string; to?: string }>({ from: daysAgoIso(30), to: todayIso() });
  const [data, setData] = useState<BusinessReport | null>(null);
  const [winLoss, setWinLoss] = useState<WinLossSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [digest, setDigest] = useState<DigestSubscription | null>(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const [salesCurrency, setSalesCurrency] = useState('');
  const currencyRows = data?.sales.byCurrency ?? [];
  const salesRow = currencyRows.find(row => row.currency === salesCurrency) ?? currencyRows[0];
  const salesMoney = (amount?: number) => amount === undefined ? '—' : `${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${salesRow?.currency === 'UNKNOWN' ? '(currency unavailable)' : salesRow?.currency ?? ''}`;

  useEffect(() => {
    reportsService.getDigest().then(setDigest).catch(() => undefined);
  }, []);

  const onSubscribe = async (frequency: 'daily' | 'weekly' | 'monthly') => {
    setDigestBusy(true);
    try {
      const r = await reportsService.subscribeDigest(frequency);
      setDigest({
        subscriptionUid: r.subscriptionUid,
        frequency: r.frequency,
        enabled: r.enabled,
        nextSendAt: r.nextSendAt as unknown as string,
        totalSent: r.totalSent,
      });
      toast.success(`Subscribed to ${frequency} digest`);
    } catch { toast.error('Failed to subscribe'); } finally { setDigestBusy(false); }
  };

  const onUnsubscribe = async () => {
    setDigestBusy(true);
    try {
      await reportsService.unsubscribeDigest();
      setDigest(digest ? { ...digest, enabled: false } : null);
      toast.success('Unsubscribed');
    } catch { toast.error('Failed to unsubscribe'); } finally { setDigestBusy(false); }
  };

  const onSendNow = async () => {
    setDigestBusy(true);
    try {
      await reportsService.sendDigestNow();
      toast.success('Digest sent — check your inbox');
    } catch { toast.error('Send failed — check email account is configured'); } finally { setDigestBusy(false); }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.all([
      reportsService.business(filter),
      reportsService.winLoss(filter).catch(() => null),
    ])
      .then(([biz, wl]) => {
        if (!alive) return;
        setData(biz);
        setWinLoss(wl);
      })
      .catch((e: unknown) => {
        if (alive) {
          const msg = e instanceof Error ? e.message : 'Failed to load report';
          setError(msg);
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [filter]);

  const applyPreset = (p: DateRange) => {
    setActive(p.label);
    setFilter({ from: p.from, to: p.to });
  };

  const downloadPdf = async () => {
    const preview = window.open('about:blank', '_blank');
    if (!preview) { toast.error('Allow pop-ups to open the printable report'); return; }
    preview.opener = null;
    try {
      const blob = await reportsService.businessHtml(filter);
      const url = URL.createObjectURL(new Blob([blob], { type: 'text/html' }));
      preview.location.href = url;
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { preview.close(); }
  };

  return (
    <div>
      <Topbar
        title="Business reports"
        breadcrumb="Insights"
        description="Aggregate view of leads, sales, campaigns, and AI usage. Print or download as PDF."
        right={
          <div className="flex items-center gap-2 flex-wrap">
            {PRESETS.map((p) => (
              <Button
                key={p.label}
                size="sm"
                variant={active === p.label ? 'primary' : 'secondary'}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </Button>
            ))}
            <Button size="sm" iconLeft={<Download size={14} />} onClick={downloadPdf}>
              Download PDF
            </Button>
          </div>
        }
      />

      <div className="px-7 py-5 space-y-5">
        {/* Digest subscription — top of page so it's discoverable */}
        <Card padded className="border-primary/20 bg-primary-soft/20">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                <Mail size={16} />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-bold text-text-primary">Email me this report</div>
                {digest?.enabled ? (
                  <div className="text-2xs text-text-muted">
                    Subscribed — {digest.frequency} · next send {new Date(digest.nextSendAt).toLocaleString()}
                  </div>
                ) : (
                  <div className="text-2xs text-text-muted">Get a weekly summary in your inbox.</div>
                )}
                {digest?.lastError && (
                  <div className="text-2xs text-danger mt-0.5">Last send failed: {digest.lastError}</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {digest?.enabled ? (
                <>
                  <Select
                    value={digest.frequency}
                    onChange={(e) => onSubscribe(e.target.value as 'daily' | 'weekly' | 'monthly')}
                    disabled={digestBusy}
                    className="text-xs"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </Select>
                  <Button size="sm" variant="secondary" onClick={onSendNow} loading={digestBusy}>
                    <Send size={12} /> Send now
                  </Button>
                  <Button size="sm" variant="ghost" onClick={onUnsubscribe} loading={digestBusy}>
                    Unsubscribe
                  </Button>
                </>
              ) : (
                <Button size="sm" onClick={() => onSubscribe('weekly')} loading={digestBusy} iconLeft={<Mail size={12} />}>
                  Subscribe (weekly)
                </Button>
              )}
            </div>
          </div>
        </Card>

        {error && (
          <Card padded>
            <div className="flex items-start gap-2.5">
              <AlertCircle size={16} className="mt-0.5 text-danger" />
              <div>
                <div className="text-sm font-semibold text-text-primary">Couldn't load report</div>
                <p className="text-xs text-text-muted mt-0.5">{error}</p>
              </div>
            </div>
          </Card>
        )}

        {loading && !data && (
          <Card padded><div className="py-12 text-center text-sm text-text-muted">Generating report…</div></Card>
        )}

        {data && (
          <>
            <Card padded className="bg-primary-soft/30">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-2xs font-semibold uppercase tracking-wider text-text-muted">Business report</div>
                  <h2 className="mt-0.5 text-lg font-bold text-text-primary">{data.company.name}</h2>
                  <p className="text-xs text-text-muted">{data.company.tagline ?? data.company.industry ?? ''}</p>
                </div>
                <div className="text-right text-xs text-text-muted">
                  <div className="flex items-center justify-end gap-1.5"><Calendar size={11} /> {data.range.label}</div>
                  <div>Generated {new Date(data.generatedAt).toLocaleString()}</div>
                </div>
              </div>
            </Card>

            {/* ── Leads ─────────────────────────────────────────────────────── */}
            <Card padded>
              <SectionHeader title="Leads" icon={<Users size={16} />} />
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatTile label="Total leads" value={fmtNum(data.leads.total)} icon={<Users size={14} />} accent="gold" />
                <StatTile label="→ Qualified" value={fmtPct(data.leads.conversionToQualified)} icon={<Trophy size={14} />} accent="blue" />
                <StatTile label="→ Won" value={fmtPct(data.leads.conversionToWon)} icon={<Trophy size={14} />} accent="green" />
                <StatTile label="Sources" value={fmtNum(data.leads.bySource.length)} icon={<BarChart3 size={14} />} accent="amber" />
              </div>

              {data.leads.bySource.length > 0 && (
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <Mini title="By source">
                    {data.leads.bySource.slice(0, 8).map((s) => (
                      <Row key={s.source} label={s.source} value={fmtNum(s.count)} />
                    ))}
                  </Mini>
                  <Mini title="By status">
                    {Object.entries(data.leads.byStatus).map(([k, v]) => (
                      <Row key={k} label={k} value={fmtNum(v)} />
                    ))}
                  </Mini>
                </div>
              )}
            </Card>

            {!!data.sales.unresolvedOutcomes && <div className="rounded-xl border border-warning/30 bg-warning-soft p-4 text-sm text-text-secondary">{data.sales.unresolvedOutcomes} older deals have inconsistent stage or closure data. Their outcomes are excluded until reviewed.</div>}
            {/* ── Sales ─────────────────────────────────────────────────────── */}
            <Card padded>
              <div className="mb-4 flex flex-wrap justify-between gap-3"><SectionHeader title="Sales" icon={<Trophy size={16} />} /><Select aria-label="Sales currency" value={salesRow?.currency ?? ''} onChange={event => setSalesCurrency(event.target.value)} className="!w-auto">{currencyRows.length ? currencyRows.map(row => <option key={row.currency} value={row.currency}>{row.currency === 'UNKNOWN' ? 'Currency unavailable' : row.currency}</option>) : <option value="">No values</option>}</Select></div>
              <p className="mb-3 text-xs text-text-muted">Outcome counts include all currencies. Values stay in the selected currency; no exchange-rate conversion is applied.</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatTile label="Deals created" value={fmtNum(data.sales.dealsCreated)} icon={<Trophy size={14} />} accent="gold" />
                <StatTile label="Won" value={fmtNum(data.sales.dealsWon)} delta={`${data.sales.dealsLost} lost`} trend="up" icon={<Trophy size={14} />} accent="green" />
                <StatTile label="Revenue won" value={salesMoney(salesRow?.revenueWon)} icon={<BarChart3 size={14} />} accent="green" />
                <StatTile label="Avg deal size" value={salesMoney(salesRow?.averageWonDeal)} icon={<BarChart3 size={14} />} accent="blue" />
              </div>
              <div className="mt-3 rounded-lg border border-border bg-surface-alt/40 px-4 py-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Open pipeline value</span>
                  <span className="text-base font-semibold text-text-primary">{salesMoney(salesRow?.pipelineValue)}</span>
                </div>
                <p className="mt-1 text-2xs text-text-muted">Current open deals across all pipelines in the selected currency.</p>
              </div>
            </Card>

            {/* ── Win / Loss ────────────────────────────────────────────────── */}
            {winLoss && (winLoss.totals.won + winLoss.totals.lost > 0) && (
              <Card padded>
                <SectionHeader
                  title="Win / Loss"
                  subtitle={`${winLoss.totals.won} won · ${winLoss.totals.lost} lost · ${winLoss.totals.open} open`}
                  icon={<TrendingUp size={16} />}
                />

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <StatTile
                    label="Win rate"
                    value={`${winLoss.totals.winRatePct}%`}
                    delta={winLoss.totals.winRatePct >= 50 ? 'healthy' : 'below 50%'}
                    trend={winLoss.totals.winRatePct >= 50 ? 'up' : 'down'}
                    icon={<Trophy size={14} />}
                    accent={winLoss.totals.winRatePct >= 50 ? 'green' : 'gold'}
                  />
                  <StatTile
                    label="Avg days to close"
                    value={winLoss.totals.avgDaysToClose != null ? String(winLoss.totals.avgDaysToClose) : '—'}
                    icon={<Calendar size={14} />}
                    accent="blue"
                  />
                  <StatTile
                    label="Won"
                    value={fmtNum(winLoss.totals.won)}
                    icon={<Trophy size={14} />}
                    accent="green"
                  />
                  <StatTile
                    label="Lost"
                    value={fmtNum(winLoss.totals.lost)}
                    icon={<XIcon size={14} />}
                    accent={winLoss.totals.lost > 0 ? 'red' : 'gold'}
                  />
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  {/* Top loss reasons */}
                  <div>
                    <div className="mb-2 text-2xs font-semibold uppercase tracking-wider text-text-muted">
                      Top lost-deal reasons
                    </div>
                    {winLoss.lostReasons.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-border bg-surface-alt/40 p-4 text-center text-xs text-text-muted">
                        No reasons captured. Choose a reason when marking a deal as lost.
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {winLoss.lostReasons.map((r) => {
                          const max = Math.max(...winLoss.lostReasons.map((x) => x.count));
                          const widthPct = Math.round((r.count / max) * 100);
                          return (
                            <div key={`${r.reason}:${r.currency}`} className="rounded-lg border border-border bg-surface-alt/30 px-3 py-2">
                              <div className="flex items-center justify-between text-xs">
                                <span className="font-medium text-text-primary">{r.reason} · {r.currency === 'UNKNOWN' ? 'Currency unavailable' : r.currency}</span>
                                <span className="font-semibold tabular-nums text-text-secondary">{r.count}</span>
                              </div>
                              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-border">
                                <div className="h-full bg-danger" style={{ width: `${widthPct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Won value by currency */}
                  <div>
                    <div className="mb-2 text-2xs font-semibold uppercase tracking-wider text-text-muted">
                      Won value (per currency)
                    </div>
                    {Object.keys(winLoss.wonValueByCurrency).length === 0 ? (
                      <div className="rounded-lg border border-dashed border-border bg-surface-alt/40 p-4 text-center text-xs text-text-muted">
                        No won deals had a value attached.
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {Object.entries(winLoss.wonValueByCurrency).map(([cur, v]) => (
                          <div key={cur} className="flex items-center justify-between rounded-lg border border-border bg-success-soft/30 px-3 py-2">
                            <span className="text-xs font-semibold text-text-primary">{cur}</span>
                            <span className="text-base font-bold tabular-nums text-success">
                              {v.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* ── Campaigns ─────────────────────────────────────────────────── */}
            <Card padded>
              <SectionHeader title="WhatsApp delivery" subtitle="Campaign and bulk attempts in the selected period, with current receipts." icon={<Send size={16} />} />
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatTile label="Sent" value={fmtNum(data.campaigns.sent)} icon={<Send size={14} />} accent="blue" />
                <StatTile label="Delivered" value={fmtPct(data.campaigns.deliveryRate)} delta={`${fmtNum(data.campaigns.delivered)}`} icon={<Send size={14} />} accent="blue" />
                <StatTile label="Read" value={fmtPct(data.campaigns.readRate)} delta="of delivered" icon={<Send size={14} />} accent="green" />
                <StatTile label="Failed" value={fmtNum(data.campaigns.failed)} icon={<AlertCircle size={14} />} accent={data.campaigns.failed > 0 ? 'red' : 'gold'} />
              </div>
            </Card>

            <div className="grid gap-4 xl:grid-cols-2">
              <ReportBreakdownChart title="Lead sources" description="Customers captured during the selected period." rows={data.leads.bySource.map(row => ({ name: row.source.replace(/_/g, ' '), value: row.count }))} />
              <ReportBreakdownChart overlapping title="WhatsApp delivery" description="Receipt stages overlap: a read message is also delivered." rows={[{ name: 'Sent', value: data.campaigns.sent }, { name: 'Delivered', value: data.campaigns.delivered }, { name: 'Read', value: data.campaigns.read }, { name: 'Failed', value: data.campaigns.failed }]} />
            </div>
            <Card><SectionHeader title="Advertising" subtitle="Saved campaign snapshots; currency amounts are never combined." icon={<BarChart3 size={16} />} /><div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead className="border-b border-border text-xs text-text-muted"><tr>{['Platform', 'Spend', 'Impressions', 'Clicks', 'CRM leads', 'Coverage'].map(label => <th key={label} className="px-3 py-3 font-medium">{label}</th>)}</tr></thead><tbody>{data.advertising.map(row => <tr key={`${row.platform}:${row.currency}`} className="border-b border-border last:border-0"><td className="px-3 py-3 font-medium">{row.platform}</td><td className="px-3 py-3 tabular-nums">{row.snapshotRows ? `${row.spend.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${row.currency === 'UNKNOWN' ? '(currency unavailable)' : row.currency}` : 'Not synced'}</td><td className="px-3 py-3 tabular-nums">{row.snapshotRows ? row.impressions.toLocaleString() : '—'}</td><td className="px-3 py-3 tabular-nums">{row.snapshotRows ? row.clicks.toLocaleString() : '—'}</td><td className="px-3 py-3 tabular-nums">{row.capturedLeads.toLocaleString()}</td><td className="px-3 py-3 text-xs text-text-muted">{row.coveredDays} days{row.lastSyncedAt && <span className="block">Synced {new Date(row.lastSyncedAt).toLocaleDateString()}</span>}</td></tr>)}</tbody></table></div><p className="mt-3 text-xs text-text-muted">CRM leads are captures per platform, separate from provider conversions. Lead counts repeat when a platform has multiple currencies; do not add those rows.</p></Card>
            {/* ── AI ─────────────────────────────────────────────────────────── */}
            <Card padded>
              <SectionHeader title="AI usage" subtitle={`${data.ai.succeeded} succeeded · ${data.ai.failed} failed · ${data.ai.pending} pending · ${data.ai.chargedCredits} credits charged`} icon={<Sparkles size={16} />} />
              {!!data.ai.unpricedRequests && <p className="mb-3 text-xs text-text-muted">{data.ai.unpricedRequests} requests have no final cost; the displayed estimate is partial.</p>}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatTile label="AI requests" value={fmtNum(data.ai.totalReplies)} icon={<Sparkles size={14} />} accent="gold" />
                <StatTile label="Logged qualifications" value={fmtNum(data.ai.qualifiedByAi)} icon={<Trophy size={14} />} accent="green" />
                <StatTile label="Tokens" value={fmtNum(data.ai.totalTokens)} icon={<BarChart3 size={14} />} accent="blue" />
                <StatTile label="Recorded cost estimate" value={data.ai.totalCostUsd == null ? 'Unavailable' : `$${data.ai.totalCostUsd.toFixed(2)}`} icon={<BarChart3 size={14} />} accent="amber" />
              </div>
            </Card>

            {/* ── Documents ─────────────────────────────────────────────────── */}
            <Card padded>
              <SectionHeader title="Documents" icon={<FileText size={16} />} />
              <div className="grid grid-cols-2 gap-3">
                <StatTile label="Quotations created" value={fmtNum(data.documents.quotations)} icon={<FileText size={14} />} accent="gold" />
                <StatTile label="Invoices created" value={fmtNum(data.documents.invoices)} icon={<FileText size={14} />} accent="blue" />
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function Mini({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-surface-alt/30 p-3">
      <div className="mb-2 text-2xs font-semibold uppercase tracking-wider text-text-muted">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="capitalize text-text-secondary">{label}</span>
      <span className="font-semibold tabular-nums text-text-primary">{value}</span>
    </div>
  );
}
